import { useCallback, useMemo, useRef } from 'react';

import { fromPromise } from 'xstate5';

import { type DeliveryOrderDetailInput } from '@sg/graphql-schema';
import {
  type CustomizationMachineTypes,
  reportOutOfStockIngredients,
  useCustomizationMachineLogic,
} from '@sg/shared-fe-features-customization';

import { useLastInteractedStore } from '../../../../context';
import { useAddBundleProductToBag } from '../useAddBundleProductToBag';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Provides the bundle machine with the actor that adds its products to the bag.
 */
export const useBundleAddToBagActors = () => {
  const lastInteractedStore = useLastInteractedStore();
  const addBundleProductToBag = useAddBundleProductToBag();

  // ─── Refs ────────────────────────────────────────────────────────────

  const deliveryDetailsRef = useRef<DeliveryOrderDetailInput | undefined>(undefined);

  // NOTE: Spawned customization machines keep the actors they started with, so
  //       their add reads the latest one through this ref.
  const addBundleProductToBagRef = useRef(addBundleProductToBag);

  addBundleProductToBagRef.current = addBundleProductToBag;

  // ─── Derived ─────────────────────────────────────────────────────────

  const customizationImplementations = useMemo(() => {
    return {
      actors: {
        addToBag: fromPromise<void, AddToBagInput>(async (args) => {
          await addBundleProductToBagRef.current({
            ...args.input,
            deliveryDetails: deliveryDetailsRef.current,
          });
        }),
      },

      // NOTE: Out-of-stock default ingredients are left off, and the bundle shows them on the
      //       selected product rather than asking first.
      actions: { onOutOfStockIngredientsFound: reportOutOfStockIngredients },
    };
  }, []);

  const customization = useCustomizationMachineLogic(customizationImplementations);

  // ─── Handlers ────────────────────────────────────────────────────────

  /**
   * Freezes where the products go for the rest of the add, so a store change
   * midway can't split the bundle across orders.
   */
  const captureDeliveryDetails = useCallback(() => {
    deliveryDetailsRef.current = getDeliveryDetails(lastInteractedStore);
  }, [lastInteractedStore]);

  // ─────────────────────────────────────────────────────────────────────

  return { customization, captureDeliveryDetails };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getDeliveryDetails(
  lastInteractedStore: LastInteractedStore,
): DeliveryOrderDetailInput | undefined {
  const { addressId, deliveryFee, vendor, vendorRestaurantId } = lastInteractedStore ?? {};

  if (!addressId) return;

  return {
    tip: 0,
    addressId,
    vendor: vendor ?? '',
    vendorRestaurantId: vendorRestaurantId ?? '',
    deliveryFee: deliveryFee ?? 0,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type AddToBagInput = CustomizationMachineTypes.AddToBagInput;

type LastInteractedStore = ReturnType<typeof useLastInteractedStore>;
